import { ContentRenderer } from "@/lib/content/ContentRenderer";
import { ContentToken } from "@/lib/content/types";
import { getSubjectColorIndex } from "@/lib/strapi/formatters";
import {
  SubTopicReduxSchema,
  TopicTestReduxSchema,
  VideoReduxSchema,
} from "@/types/redux-schema";
import {
  Badge,
  Box,
  Button,
  Card,
  Menu,
  Spoiler,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import ArrowDropDownRounded from "@mui/icons-material/ArrowDropDownRounded";
import DownloadRounded from "@mui/icons-material/DownloadRounded";
import ScheduleSendRounded from "@mui/icons-material/ScheduleSendRounded";
import { Resources } from "@tsTypes/subject-schema";
import clsx from "clsx";
import classes from "./IndividualResourceCard.module.css";
import ProgressTrackerChip from "./ProgressTrackerChip";
import { ResourceNotAvailablePlaceholder } from "./ResourceNotAvailablePlaceholder";

type IndividualResourceCardProps =
  | {
      resource: Extract<Resources, "ia examples">;
      hiddenByFilters: boolean;
      subjectNameLower: string;
      subjectDocumentId: string;
      sneakPeakContent: JSX.Element | undefined;
      topRightChipCopy: string | undefined;
      preTitle: string;
      title: string;
      description: ContentToken[] | string;
      resourceLinks: string[];
      disableActions: boolean;
      resourceReduxMeta?: undefined;
      actionOnClick: (resourceIndex: number) => void;
    }
  | {
      resource: Extract<Resources, "cram sheets">;
      hiddenByFilters: boolean;
      subjectNameLower: string;
      subjectDocumentId: string;
      sneakPeakContent: JSX.Element | undefined;
      topRightChipCopy: string | undefined;
      preTitle: string;
      title: string;
      description: ContentToken[] | string;
      resourceLinks: string[] | undefined; // in case of placeholder card
      disableActions: boolean;
      resourceReduxMeta: SubTopicReduxSchema["cramSheets"];
      actionOnClick: (resourceIndex: number) => void;
    }
  | {
      resource: Extract<Resources, "topic tests">;
      hiddenByFilters: boolean;
      subjectNameLower: string;
      subjectDocumentId: string;
      sneakPeakContent: JSX.Element | undefined;
      topRightChipCopy: string | undefined;
      preTitle: string;
      title: string;
      description: ContentToken[] | string;
      resourceLinks: string[] | undefined;
      disableActions: boolean;
      resourceReduxMeta: TopicTestReduxSchema | undefined;
      actionOnClick: (resourceIndex: number) => void;
    }
  | {
      resource: Extract<Resources, "videos">;
      hiddenByFilters: boolean;
      subjectNameLower: string;
      subjectDocumentId: string;
      sneakPeakContent: JSX.Element | undefined;
      topRightChipCopy: string | undefined;
      preTitle: string;
      title: string;
      description: ContentToken[] | string;
      resourceLinks?: undefined;
      disableActions: boolean;
      resourceReduxMeta: VideoReduxSchema | undefined;
      actionOnClick?: undefined;
    };
export default function IndividualResourceCard({
  resource,
  hiddenByFilters,
  subjectNameLower,
  subjectDocumentId,
  sneakPeakContent,
  topRightChipCopy,
  preTitle,
  title,
  description,
  resourceLinks,
  disableActions,
  resourceReduxMeta,
  actionOnClick,
}: IndividualResourceCardProps) {
  const subjectColorIndex = getSubjectColorIndex(subjectNameLower);
  const splitButton = resourceLinks ? resourceLinks.length > 1 : false;
  const isVideoPage = resource === "videos";
  return (
    <Card
      withBorder
      className={clsx(
        "definitionBoundary",
        classes.card,
        isVideoPage && classes.cardVideoPage,
        splitButton && classes.cardSplitButton,
        hiddenByFilters && classes.cardHidden
      )}
    >
      {/* Preview section */}
      <Box className={classes.previewSection}>
        {sneakPeakContent ? (
          sneakPeakContent
        ) : (
          <ResourceNotAvailablePlaceholder
            reason="in development"
            resource={resource}
            subjectColorIndex={subjectColorIndex}
          />
        )}
        <Box className={classes.gradientOverlay} />
        {topRightChipCopy && (
          <Badge
            className={classes.topRightChipCopy}
            color={"darkblue"}
            variant="filled"
            size="md"
          >
            {topRightChipCopy}
          </Badge>
        )}
      </Box>
      <Stack gap="xl" p="lg" className={classes.contentStack}>
        {/* Pre title and title */}
        <Box className={classes.preTitleAndTitle}>
          <Text size="xs" tt="uppercase" className={classes.preTitle}>
            {preTitle}
          </Text>
          <Title
            order={3}
            lh={1.4}
            tt="capitalize"
            className={classes.title}
          >
            {title}
          </Title>
        </Box>
        {/* Description */}
        <Spoiler
          maxHeight={125}
          mih={resource === "topic tests" ? 50 : 125}
          showLabel="Read more"
          hideLabel="Hide"
          className={classes.descriptionSpoiler}
          mb={resource === "videos" ? "xl" : undefined}
        >
          <Text component="div" size="sm" className={classes.descriptionText}>
            <ContentRenderer content={description} />
          </Text>
        </Spoiler>
        {/* CTA buttons */}
        <div className={classes.buttonContainer}>
          {resourceLinks && (
            <>
              <Button
                aria-label={getCTATitleForResource(
                  resource,
                  resourceLinks.length
                )}
                disabled={disableActions}
                color={subjectColorIndex}
                variant="filled"
                className={classes.mainButton}
                leftSection={getCTAIconForResource(resource)}
                onClick={() => actionOnClick(0)}
              >
                {getCTATitleForResource(resource, resourceLinks.length)}
              </Button>

              {resourceLinks.length > 1 && (
                <Menu shadow="md" width={200} position="bottom-end">
                  <Menu.Target>
                    <Button
                      aria-label="More options"
                      className={classes.dropdownButton}
                      disabled={disableActions}
                      color={subjectColorIndex}
                      variant="filled"
                      px="xs"
                    >
                      <ArrowDropDownRounded className={classes.dropdownIcon} />
                    </Button>
                  </Menu.Target>

                  <Menu.Dropdown>
                    {resourceLinks.slice(1).map((link, index) => {
                      return (
                        <Menu.Item
                          aria-label={getCTAMenuTitlesForResource(
                            resource,
                            index
                          )}
                          key={resource + title + link + index}
                          leftSection={getCTAIconForResource(resource)}
                          onClick={() => actionOnClick(index + 1)}
                        >
                          {getCTAMenuTitlesForResource(resource, index)}
                        </Menu.Item>
                      );
                    })}
                  </Menu.Dropdown>
                </Menu>
              )}
            </>
          )}
        </div>
        {resource !== "ia examples" && (
          <ProgressTrackerChip
            resource={resource}
            resourceReduxMeta={resourceReduxMeta}
            subjectName={subjectNameLower}
            subjectDocumentId={subjectDocumentId}
            noResource={!sneakPeakContent}
            disableActions={disableActions}
          />
        )}
      </Stack>
    </Card>
  );
}

export function getResourceNiceName(resource: Resources) {
  switch (resource) {
    case "cram sheets":
      return "Cram Sheet";
    case "topic tests":
      return "Topic Test";
    case "videos":
      return "Video";
    case "flashcards":
      return "Flashcards";
    case "notes":
      return "Notes";
    case "questions":
      return "Questions";
    case "ia examples":
      return "IA Example";
    case "ia criteria":
      return "IA Criteria";
    case "command terms":
      return "Command Term";
    case "exam breakdown":
      return "Exam Breakdown";
    case "test builder":
      return "Test Builder";
    case "mock exams":
      return "Mock Exam";
    case "individual topic test":
      return "Topic Test";
    default:
      return "Resource";
  }
}

function getCTATitleForResource(
  resource: Extract<Resources, "ia examples" | "cram sheets" | "topic tests">,
  numberOfLinks: number
) {
  switch (resource) {
    case "ia examples":
      return "Student PDF";
    case "cram sheets":
      return numberOfLinks === 1 ? "Download Sheet" : "Download Sheet #1";
    case "topic tests":
      return "Start Test";
  }
}

function getCTAMenuTitlesForResource(
  resource: Extract<Resources, "ia examples" | "cram sheets" | "topic tests">,
  index: number
) {
  switch (resource) {
    case "ia examples":
      return `Examiner PDF`;
    case "cram sheets":
      return `Download Sheet #${index + 2}`;
    case "topic tests":
      return `Start Test Shuffled`;
  }
}

function getCTAIconForResource(
  resource: Extract<Resources, "ia examples" | "cram sheets" | "topic tests">
) {
  const iconProps = {
    width: 20,
    height: 20,
  };
  switch (resource) {
    case "ia examples":
      return <DownloadRounded style={iconProps} />;
    case "cram sheets":
      return <DownloadRounded style={iconProps} />;
    case "topic tests":
      return <ScheduleSendRounded style={iconProps} />;
  }
}
